import { Col, Form, List, Row } from 'antd';
import CustomInput from '@/components/CustomInput';
import CustomTitle from '@/components/CustomTitle';
import CommonFooter from '@/components/CommonFooter';
import type { CredentialEditorFormProps } from '../types';

const CREDENTIAL_NAME_MAX_LENGTH = 50;
const CREDENTIAL_DESCRIPTION_MAX_LENGTH = 300;
const HAS_LETTER_REGEX = /[A-Za-z]/;

const CredentialEditorForm: React.FC<CredentialEditorFormProps> = ({
  credentialForm,
  credentialSets,
  editingCredentialIndex,
  instructions,
  onCancel,
  onFinish,
  requiredInputs,
}) => (
  <Form
    key={
      editingCredentialIndex === null
        ? 'add-credentials'
        : `edit-credentials-${credentialSets[editingCredentialIndex]?.local_key}`
    }
    form={credentialForm}
    onFinish={onFinish}
    layout='vertical'
    preserve={false}
    className='sensor-credential-form'
  >
    <div className='custom-scrollbar sensor-credential-scroll'>
      <Row gutter={[16, 14]}>
        <Col span={24}>
          <CustomTitle
            text={editingCredentialIndex === null ? 'Add' : 'Edit'}
            fontSize={20}
            fontWeight='700'
            color='var(--text-primary)'
            marginBottom={0}
          />
        </Col>
        <Col span={24}>
          <Form.Item
            className='custom-input form-input-style'
            label='Name'
            name='name'
            rules={[
              { required: true, message: 'Please enter name' },
              {
                max: CREDENTIAL_NAME_MAX_LENGTH,
                message: `Name cannot exceed ${CREDENTIAL_NAME_MAX_LENGTH} characters`,
              },
              {
                validator: (_, value) => {
                  if (!value || HAS_LETTER_REGEX.test(value)) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error('Name must contain at least one letter')
                  );
                },
              },
            ]}
          >
            <CustomInput
              bgColor='var(--light-gray)'
              height='36px'
              textColor='var(--text-primary)'
              borderRadius='8px'
              border='none'
              maxLength={CREDENTIAL_NAME_MAX_LENGTH}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            className='custom-input form-input-style'
            label='Description'
            name='description'
            rules={[
              {
                max: CREDENTIAL_DESCRIPTION_MAX_LENGTH,
                message: `Description cannot exceed ${CREDENTIAL_DESCRIPTION_MAX_LENGTH} characters`,
              },
            ]}
          >
            <CustomInput
              bgColor='var(--light-gray)'
              height='36px'
              textColor='var(--text-primary)'
              borderRadius='8px'
              border='none'
              maxLength={CREDENTIAL_DESCRIPTION_MAX_LENGTH}
            />
          </Form.Item>
        </Col>

        {instructions.length > 0 && (
          <Col span={24}>
            <CustomTitle
              text='Instructions'
              fontSize='14px'
              fontWeight='600'
              color='black'
            />
            <List
              className='sensor-instructions-list'
              split={false}
              itemLayout='horizontal'
              dataSource={instructions}
              renderItem={(item, index) => (
                <List.Item className='sensor-list-item-flush'>
                  <List.Item.Meta
                    title={
                      item.isLink ? (
                        <>
                          <span className='sensor-instruction-text'>
                            {index + 1}. {item.title}{' '}
                          </span>
                          <a
                            href={item?.href}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='sensor-instruction-link'
                          >
                            {item.linkText}
                          </a>
                        </>
                      ) : (
                        <span className='sensor-instruction-text'>
                          {index + 1}. {item?.title}
                        </span>
                      )
                    }
                  />
                </List.Item>
              )}
            />
          </Col>
        )}

        {requiredInputs.map((element) => (
          <Col span={24} key={element}>
            <Form.Item
              className='custom-input form-input-style'
              label={element}
              name={element}
              rules={[
                {
                  required: true,
                  message: `Please input ${element ? element : 'value'}`,
                },
              ]}
            >
              <CustomInput
                placeholder={`Paste ${element ? element : 'value'} here`}
                height='36px'
                width='100%'
                border='0px'
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='38px'
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    </div>

    <div className='sensor-drawer-footer'>
      <CommonFooter
        text={
          editingCredentialIndex === null
            ? 'Add credentials'
            : 'Save credentials'
        }
        onSave={() => credentialForm.submit()}
        handleCancel={onCancel}
      />
    </div>
  </Form>
);

export default CredentialEditorForm;
